import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  LayoutDashboard,
  Building2,
  MonitorSmartphone,
  AlertTriangle,
  UserCog,
  UsersRound,
  ShieldCheck,
  ShieldAlert,
  KeyRound,
  FileText,
  Search,
  Settings,
  Monitor,
  Zap,
  Bell,
  ClipboardList,
  X,
  Check,
  Power,
  Cog,
  Menu,
  Cpu,
  MemoryStick,
  HardDrive,
  RefreshCw,
  Sparkles,
  Router,
  Globe,
} from 'lucide-react';
import { api, logout } from '../lib/api.jsx';
import {
  RealtimeProvider,
  useRealtime,
  useRealtimeState,
  useRealtimeDisconnect,
  useCoalesced,
} from '../lib/realtime.jsx';
import { hasPermission } from '../lib/permissions.jsx';
import { parseHash, viewToHash } from '../lib/router.jsx';
import { ThemeToggle, useTheme } from '../lib/theme.jsx';
import { fmtMachineType, fmtOnlineStatus, fmtOsName, onlineSortValue } from '../lib/format.jsx';
import { DeviceDetail } from './DeviceDetail.jsx';
import { ClientsView } from './Clients.jsx';
import { ClientDetail } from './ClientDetail.jsx';
import { ProvisioningView } from './Provisions.jsx';
import { DashboardContext } from '../views/dashboardContext.jsx';
import { AlertsTable } from '../components/AlertsTable.jsx';
import { MachineTypeBadge } from '../components/MachineTypeBadge.jsx';
import { ConfigPage } from '../views/ConfigPage.jsx';
import { UsersPage } from '../views/UsersPage.jsx';
import { AlertsPage } from '../views/AlertsPage.jsx';
import { GroupsPage } from '../views/GroupsPage.jsx';
import { RolesPage } from '../views/RolesPage.jsx';
import { ProfilePage } from '../views/ProfilePage.jsx';
import { AuditPage } from '../views/AuditPage.jsx';
import { SearchPage } from '../views/SearchPage.jsx';
import { VulnerabilitiesPage } from '../views/VulnerabilitiesPage.jsx';
import { AiReportsPage } from '../views/AiReportsPage.jsx';
import { MikrotikPage } from '../views/MikrotikPage.jsx';
import { WebMonitorsPage } from '../views/WebMonitorsPage.jsx';
import { RemoteViewerPage } from '../views/RemoteViewerPage.jsx';
import { ClientPicker } from '../components/ClientPicker.jsx';
import { WindowsIcon, TuxIcon, RouterIcon } from '../components/PlatformIcons.jsx';
import { PilotBadge, isPilot, normalizeChannel } from '../components/UpdateChannelControl.jsx';

// Libellés courts pour les toasts « commande terminée » — cf. CommandRecord.Type
// (Models/Entities.cs) : 0=Shell,1=Redémarrage,2=Arrêt,3=MAJ Windows,4=Script,
// 6=MAJ de paquets (agent Linux). Une valeur absente de cette table retombe
// sur « Commande » plutôt que d'planter.
const COMMAND_TYPE_LABELS = {
  0: 'Commande shell',
  1: 'Redémarrage',
  2: 'Arrêt',
  3: 'Mise à jour Windows',
  4: 'Script',
  6: 'Mise à jour de paquets',
};

// Tons sémantiques partagés par la cloche d'alertes et la pile de toasts —
// jamais de couleur en dur (cf. Common gotchas de CLAUDE.md).
const TONE_DOT = { crit: 'bg-crit', warn: 'bg-warn', ok: 'bg-ok' };
const TONE_TEXT = { crit: 'text-crit', warn: 'text-warn', ok: 'text-ok' };
const TONE_BORDER = { crit: 'border-crit/40', warn: 'border-warn/40', ok: 'border-ok/40' };

const RT_STATE_LABEL = {
  connected: 'Temps réel connecté',
  reconnecting: 'Reconnexion…',
  disconnected: 'Temps réel déconnecté',
};
const RT_STATE_DOT = {
  connected: 'bg-ok',
  reconnecting: 'bg-warn animate-pulse',
  disconnected: 'bg-crit',
};

// ─── Main Dashboard (API-connected) ──────────────────────────
// Enrobe le corps dans <RealtimeProvider> : la connexion SignalR vit tout le
// temps que le composant est monté, soit toute la session connectée (Dashboard
// n'est rendu par App.jsx qu'après login). `DashboardBody` est un enfant du
// Provider — c'est ce qui lui permet de consommer `useRealtime` /
// `useRealtimeState` : un composant ne peut pas lire le contexte qu'il crée
// lui-même dans son propre rendu.
export function Dashboard(props) {
  // Visionneuse de prise en main : seule, plein cadre, sans barre latérale ni
  // connexion SignalR. L'onglet est dédié (window.open) et ne change jamais de vue.
  const remote = /^#\/remote\/([^/?]+)/.exec(window.location.hash);
  if (remote) {
    return (
      <>
        <RemoteViewerPage sessionId={decodeURIComponent(remote[1])} user={props.user} perms={props.perms} />
      </>
    );
  }
  return (
    <RealtimeProvider>
      <DashboardBody {...props} />
    </RealtimeProvider>
  );
}

function DashboardBody({ user, perms, reloadPerms }) {
  const can = (name) => hasPermission(perms, name);
  const [theme] = useTheme();
  const brandLogo =
    theme === 'light'
      ? '/branding/fonxtio/Logo_couleur_fonce-12.svg'
      : '/branding/fonxtio/Logo_couleur_pale_telipso.svg';
  const [data, setData] = useState(null);
  const initial = useMemo(() => parseHash(), []);
  const [view, setView] = useState(initial.view);
  const [selectedDevice, setSelectedDevice] = useState(initial.selectedDevice);
  const [selectedClient, setSelectedClient] = useState(initial.selectedClient);
  const [loading, setLoading] = useState(true);

  // Sync hash → state on browser back/forward
  useEffect(() => {
    const onHashChange = () => {
      const r = parseHash();
      setView(r.view);
      if (r.selectedClient !== null) setSelectedClient(r.selectedClient);
      if (r.selectedDevice !== null) setSelectedDevice(r.selectedDevice);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // navigate: update state + push hash
  const navigate = (newView, opts = {}) => {
    const nc = opts.client !== undefined ? opts.client : newView === 'clientDetail' ? selectedClient : null;
    const nd = opts.device !== undefined ? opts.device : newView === 'deviceDetail' ? selectedDevice : null;
    if (opts.client !== undefined) setSelectedClient(opts.client);
    if (opts.device !== undefined) setSelectedDevice(opts.device);
    setView(newView);
    const hash = viewToHash(newView, nc, nd);
    if (window.location.hash !== hash) window.location.hash = hash;
    setMobileMenuOpen(false); // ferme le menu mobile après navigation
  };
  const viewRef = useRef(view);
  viewRef.current = view;
  // CVE summary affiché dans les cartes du tableau de bord (cliquables vers
  // la page Vulnérabilités). Endpoint dédié pour ne pas alourdir /dashboard.
  const [cveSummary, setCveSummary] = useState(null);

  // ─── Temps réel : état de connexion + pile de toasts ────────────
  const rtState = useRealtimeState();
  const disconnectRealtime = useRealtimeDisconnect();
  const [toasts, setToasts] = useState([]);
  const toastIdRef = useRef(0);
  const toastTimersRef = useRef(new Map());
  const dismissToast = useCallback((id) => {
    const t = toastTimersRef.current.get(id);
    if (t) {
      clearTimeout(t);
      toastTimersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);
  const pushToast = useCallback(
    (toast) => {
      const id = ++toastIdRef.current;
      setToasts((prev) => [...prev, { id, ...toast }]);
      toastTimersRef.current.set(
        id,
        setTimeout(() => dismissToast(id), 6000)
      );
    },
    [dismissToast]
  );
  useEffect(
    () => () => toastTimersRef.current.forEach((t) => clearTimeout(t)),
    []
  );

  // ─── Cloche d'alertes (bas-droite du haut de page) ──────────────
  // `mh_alerts_seen` suit la convention `mh_theme` / `mh_nic_filter` :
  // horodatage ISO de la dernière ouverture de la cloche, en localStorage.
  const [bellOpen, setBellOpen] = useState(false);
  const [bellAlerts, setBellAlerts] = useState([]);
  const [alertsSeenAt, setAlertsSeenAt] = useState(
    () => localStorage.getItem('mh_alerts_seen') || ''
  );
  // Ids déjà vus cette session (pour ne toaster QUE les nouvelles alertes).
  // null tant que la première lecture n'est pas arrivée : on ne rejoue pas
  // l'historique existant en rafale de toasts à la connexion.
  const knownAlertIdsRef = useRef(null);
  const refreshBellAlerts = useCallback(async () => {
    if (!can('ViewDevices')) return;
    // RÈGLE DE SÉCURITÉ : on ne lit JAMAIS le payload de l'événement NewAlert
    // pour peupler la cloche ou les toasts — seul ce refetch, filtré par
    // PermissionService côté serveur, décide de ce que l'utilisateur voit.
    const r = await api.get('/platform/alerts?resolved=false&pageSize=10');
    const items = (r?.items || []).filter((a) => a.severity >= 1);
    setBellAlerts(items);
    if (knownAlertIdsRef.current === null) {
      knownAlertIdsRef.current = new Set(items.map((a) => a.id));
      return;
    }
    const known = knownAlertIdsRef.current;
    for (const a of items) {
      if (known.has(a.id)) continue;
      known.add(a.id);
      pushToast({
        tone: a.severity === 2 ? 'crit' : 'warn',
        title: a.severity === 2 ? 'Nouvelle alerte critique' : 'Nouvelle alerte',
        message: `${a.title} — ${a.computerName || a.clientName || ''}`,
      });
    }
  }, [can, pushToast]);

  const toggleBell = () => {
    setBellOpen((prev) => {
      const next = !prev;
      if (next) {
        const now = new Date().toISOString();
        localStorage.setItem('mh_alerts_seen', now);
        setAlertsSeenAt(now);
      }
      return next;
    });
  };
  const unreadAlertsCount = bellAlerts.filter(
    (a) => !alertsSeenAt || new Date(a.occurredAt) > new Date(alertsSeenAt)
  ).length;

  // ─── Chargement du tableau de bord + détection des bascules en/hors
  // ligne (toasts), sur les données déjà filtrées par accessibilité ────
  const knownOnlineRef = useRef(null); // null = pas encore initialisé
  const load = useCallback(async () => {
    const [d, cve] = await Promise.all([
      api.get('/dashboard'),
      can('ViewDevices') ? api.get('/platform/vulnerabilities/summary') : Promise.resolve(null),
    ]);
    if (d?.devices) {
      if (knownOnlineRef.current === null) {
        knownOnlineRef.current = new Map(d.devices.map((dv) => [dv.id, dv.isOnline]));
      } else {
        const prevOnline = knownOnlineRef.current;
        for (const dv of d.devices) {
          const was = prevOnline.get(dv.id);
          // Serveurs seulement : un poste qui s'éteint le soir n'est pas un
          // incident, et à l'échelle du parc ces toasts noyaient les autres.
          // Le retour en ligne suit la même règle, sinon il arriverait seul.
          if (was !== undefined && was !== dv.isOnline && dv.machineType === 'Server') {
            pushToast({
              tone: dv.isOnline ? 'ok' : 'crit',
              title: dv.isOnline ? 'Appareil de retour en ligne' : 'Appareil hors ligne',
              message: dv.label || dv.computerName,
            });
          }
          prevOnline.set(dv.id, dv.isOnline);
        }
      }
    }
    setData(d);
    if (cve) setCveSummary(cve);
    setLoading(false);
  }, [can, pushToast]);

  useEffect(() => {
    load();
    refreshBellAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sondage de secours : conservé si la connexion temps réel tombe, mais
  // espacé une fois qu'elle tient — les événements font le travail entre-temps.
  useEffect(() => {
    const ms = rtState === 'connected' ? 60000 : 15000;
    const interval = setInterval(() => {
      // Only auto-refresh when on dashboard or device list views
      if (['dashboard', 'devices', 'clientDetail'].includes(viewRef.current)) load();
    }, ms);
    return () => clearInterval(interval);
  }, [rtState, load]);

  // ─── Abonnements temps réel : signaux d'invalidation uniquement ─
  // Rafraîchit `data` (résumé tableau de bord + badges de la barre latérale)
  // quelle que soit la vue active — contrairement au sondage de secours
  // ci-dessus, restreint à quelques vues pour ne pas taper l'API en boucle.
  const scheduleRefresh = useCoalesced(load, 1500);
  const scheduleBellRefresh = useCoalesced(refreshBellAlerts, 1500);
  useRealtime('AgentStatusChanged', scheduleRefresh);
  useRealtime('Heartbeat', scheduleRefresh);
  useRealtime('InventoryUpdated', scheduleRefresh);
  useRealtime('ProvisionClaimed', scheduleRefresh);
  useRealtime('CommandCreated', scheduleRefresh);
  const onNewAlertEvent = useCallback(() => {
    scheduleRefresh();
    scheduleBellRefresh();
  }, [scheduleRefresh, scheduleBellRefresh]);
  useRealtime('NewAlert', onNewAlertEvent);

  // CommandCompleted : la charge utile de l'événement (statut inclus) n'est
  // JAMAIS affichée — seul l'id de commande sert à retrouver la ligne dans
  // la fiche appareil (`GET /api/devices/{id}`, filtrée par
  // CanAccessAgentAsync). Sans accès à cet appareil, le fetch échoue
  // silencieusement et aucun toast n'apparaît : c'est le comportement voulu.
  const pendingCommandChecksRef = useRef(new Map()); // agentId -> Set<commandId>
  const flushCommandChecks = useCoalesced(async () => {
    const pending = pendingCommandChecksRef.current;
    pendingCommandChecksRef.current = new Map();
    for (const [agentId, cmdIds] of pending) {
      const detail = await api.get('/devices/' + agentId);
      if (!detail) continue;
      const name = detail.agent?.label || detail.agent?.computerName || 'Appareil';
      for (const cmd of detail.recentCommands || []) {
        if (!cmdIds.has(cmd.id)) continue;
        if (cmd.status !== 2 && cmd.status !== 3 && cmd.status !== 4) continue; // pas encore terminé
        pushToast({
          tone: cmd.status === 2 ? 'ok' : 'crit',
          title:
            cmd.status === 2 ? 'Commande terminée' : cmd.status === 4 ? 'Commande expirée' : 'Commande en échec',
          message: `${COMMAND_TYPE_LABELS[cmd.type] || 'Commande'} — ${name}`,
        });
      }
    }
  }, 1500);
  const onCommandCompleted = useCallback(
    (commandId, agentId) => {
      const m = pendingCommandChecksRef.current;
      if (!m.has(agentId)) m.set(agentId, new Set());
      m.get(agentId).add(commandId);
      flushCommandChecks();
      scheduleRefresh();
    },
    [flushCommandChecks, scheduleRefresh]
  );
  useRealtime('CommandCompleted', onCommandCompleted);

  // Sidebar mobile (hamburger)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Tri/filtres de la table d'appareils
  const [devSort, setDevSort] = useState({
    key: 'memoryPercent',
    dir: 'desc',
  });
  const [devFilter, setDevFilter] = useState({
    status: 'all',
    clientId: 'all',
    machineType: 'all',
    channel: 'all',
    cpu: 0,
    mem: 0,
    disk: 0,
    updates: 0,
    search: '',
  });
  const devTableRef = useRef(null);

  // Filtre demandé pour la page /vulnerabilities : depuis les cartes du
  // dashboard (par sévérité) ou depuis une alerte CVE (par appareil — l'alerte
  // annonce un nombre, pas les logiciels concernés).
  const [vulnsRequest, setVulnsRequest] = useState({ key: 0, severity: null, agentId: null, agentName: '' });
  const goToVulns = (severity, agentId, agentName) => {
    setVulnsRequest({
      key: Date.now(),
      severity: severity ?? null,
      agentId: agentId ?? null,
      agentName: agentName || '',
    });
    navigate('vulnerabilities');
  };

  // Filtre demandé pour la page /alerts (depuis les cartes du dashboard)
  const [alertsRequest, setAlertsRequest] = useState({
    key: 0,
    severity: null,
    category: null,
    clientId: null,
    siteId: null,
  });
  const goToAlerts = (severity, category, opts = {}) => {
    setAlertsRequest({
      key: Date.now(),
      severity: severity ?? null,
      category: category ?? null,
      clientId: opts.clientId ?? null,
      siteId: opts.siteId ?? null,
    });
    navigate('alerts');
  };
  const focusPendingUpdates = () => {
    setDevFilter((f) => ({
      ...f,
      updates: 1,
    }));
    if (devTableRef.current)
      devTableRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
  };
  const focusDevices = () => {
    if (devTableRef.current)
      devTableRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
  };
  const toggleSort = (key) => {
    setDevSort((prev) =>
      prev.key === key
        ? {
            key,
            dir: prev.dir === 'asc' ? 'desc' : 'asc',
          }
        : {
            key,
            dir: 'asc',
          }
    );
  };
  const S = {
    sidebarCls: (open) =>
      // Desktop (md+): static, occupe sa colonne. Mobile: fixed slide-in depuis la gauche.
      `w-[250px] bg-sidebar border-r border-rule flex flex-col flex-shrink-0 z-40 ` +
      `fixed inset-y-0 left-0 transition-transform duration-200 md:static md:translate-x-0 ` +
      (open ? 'translate-x-0 shadow-2xl' : '-translate-x-full'),
    mainCls: 'flex-1 min-w-0 w-full px-4 md:px-8 py-6 overflow-y-auto max-h-screen text-fg-2 pt-16 md:pt-6',
    navBtnCls: (active) =>
      `flex items-center gap-[11px] w-full px-3.5 py-2.5 mb-0.5 rounded-[9px] border-none cursor-pointer text-[12.5px] text-left transition-colors ${
        active
          ? 'bg-blue-500/[.12] text-blue-400 font-semibold hover:bg-blue-500/[.18]'
          : 'bg-transparent text-muted font-normal hover:bg-card-hi hover:text-fg-2'
      }`,
    cardCls: 'bg-card border border-rule rounded-2xl px-5 py-4 flex-1 min-w-[160px]',
    statValCls: 'text-2xl font-bold text-fg font-mono',
    statLabelCls: 'text-[11.5px] text-muted-2 mb-2.5',
  };
  // Badges de la barre latérale : comptés sur `data`, déjà filtré par
  // accessibilité (cf. GetDashboardAsync/GetAccessibleClientIdsAsync) — même
  // rafraîchi sur événement, jamais un chiffre issu du payload de l'événement.
  const offlineDevicesCount = useMemo(
    () => (data?.devices || []).filter((d) => !d.isOnline).length,
    [data]
  );
  const navItems = [
    { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard, perm: null },
    { id: 'clients', label: 'Clients', icon: Building2, perm: 'ViewClients' },
    { id: 'devices', label: 'Appareils', icon: MonitorSmartphone, perm: 'ViewDevices', badge: offlineDevicesCount },
    { id: 'mikrotik', label: 'Routeurs', icon: Router, perm: 'ViewDevices' },
    { id: 'websites', label: 'Sites web', icon: Globe, perm: 'ViewDevices' },
    { id: 'search', label: 'Recherche', icon: Search, perm: 'ViewDevices' },
    { id: 'alerts', label: 'Alertes', icon: AlertTriangle, perm: 'ViewDevices', badge: data?.criticalAlerts || 0 },
    { id: 'vulnerabilities', label: 'Vulnérabilités', icon: ShieldAlert, perm: 'ViewDevices' },
    { id: 'ai', label: 'IA', icon: Sparkles, perm: 'ViewDevices' },
    { id: 'users', label: 'Utilisateurs', icon: UserCog, perm: 'ManageUsers' },
    { id: 'groups', label: 'Groupes', icon: UsersRound, perm: 'ManageUsers' },
    { id: 'roles', label: 'Rôles', icon: ShieldCheck, perm: 'ManageUsers' },
    { id: 'provisions', label: 'Provisionnement', icon: KeyRound, perm: 'ManageProvisioning' },
    { id: 'audit', label: 'Journal d\'audit', icon: FileText, perm: 'ViewAudit' },
    { id: 'config', label: 'Configuration', icon: Settings, perm: 'ManageConfig' },
  ].filter((item) => !item.perm || can(item.perm));
  const PBar = (p) => (
    <div className="w-full h-2 bg-rule rounded overflow-hidden">
      <div
        className="h-full rounded"
        style={{
          width: `${Math.min(p, 100)}%`,
          background: p >= 95 ? '#ef4444' : p >= 80 ? '#f59e0b' : '#22c55e',
          transition: 'width .5s',
        }}
      />
    </div>
  );
  const renderDashboard = () => {
    if (!data) return <div className="text-muted-2">Chargement...</div>;
    return (
      <div>
        <h1
          className="text-[22px] font-bold text-fg"
          style={{
            margin: '0 0 20px',
          }}
        >
          Tableau de bord
        </h1>{' '}
        <div className="flex gap-3 flex-wrap mb-[22px]">
          {[
            {
              label: 'Clients',
              value: data.totalClients,
              color: '#f59e0b',
              onClick: can('ViewClients') ? () => navigate('clients') : null,
            },
            {
              label: 'Sites',
              value: data.totalSites,
              color: '#3b82f6',
              onClick: can('ViewClients') ? () => navigate('clients') : null,
            },
            {
              label: 'Appareils',
              value: `${data.onlineDevices}/${data.totalDevices}`,
              color: '#22c55e',
              onClick: focusDevices,
            },
            {
              label: 'Critiques',
              value: data.criticalAlerts,
              color: '#ef4444',
              onClick: can('ViewDevices') ? () => goToAlerts(2, null) : null,
            },
            {
              label: 'MAJ',
              value: data.totalPendingUpdates,
              color: '#8b5cf6',
              onClick: focusPendingUpdates,
            },
            {
              label: 'Utilisateurs',
              value: data.activeUsers,
              color: '#f59e0b',
              onClick: can('ManageUsers') ? () => navigate('users') : null,
            },
            ...(cveSummary && can('ViewDevices')
              ? [
                  {
                    label: 'CVE critiques',
                    value: cveSummary.critical,
                    color: '#ef4444',
                    onClick: () => goToVulns('CRITICAL'),
                  },
                  {
                    label: 'CVE élevées',
                    value: cveSummary.high,
                    color: '#f97316',
                    onClick: () => goToVulns('HIGH'),
                  },
                  {
                    label: 'CVE total',
                    value: cveSummary.total,
                    color: '#8b5cf6',
                    onClick: () => goToVulns(null),
                  },
                ]
              : []),
          ].map((s) => (
            <div
              key={s.label}
              onClick={s.onClick || undefined}
              title={s.onClick ? 'Voir les détails' : undefined}
              className={`${S.cardCls} transition-colors ${s.onClick ? 'cursor-pointer hover:bg-card-hi' : 'cursor-default'}`}
              onMouseEnter={
                s.onClick
                  ? (e) => {
                      e.currentTarget.style.borderColor = s.color;
                    }
                  : undefined
              }
              onMouseLeave={
                s.onClick
                  ? (e) => {
                      e.currentTarget.style.borderColor = '';
                    }
                  : undefined
              }
            >
              <div className={S.statLabelCls}>{s.label}</div>
              <div className={S.statValCls} style={s.onClick ? { color: s.color } : undefined}>
                {s.value}
              </div>
            </div>
          ))}
        </div>
        {/* Page d'alertes embarquée, pré-filtrée sur les critiques actives.
            Garde tous les filtres / pagination / actions natives ; l'utilisateur
            peut ouvrir d'autres sévérités directement depuis ici. */}
        {data.criticalAlerts > 0 && can('ViewDevices') && (
          <div className="mb-5">
            <AlertsPage initialSeverity={2} embedded />
          </div>
        )}
        {
          // Data rate graph
          data.dataRate && data.dataRate.length > 0 && (
            <div className="bg-card border border-rule rounded-2xl mb-5 py-[18px] px-5">
              <div className="text-[13px] font-semibold text-fg-2 mb-3.5">
                Flux de données agents (60 min)
              </div>
              <div
                className="relative flex items-end"
                style={{
                  height: 120,
                  gap: 1,
                }}
              >
                {(() => {
                  const maxVal = Math.max(1, ...data.dataRate.map((d) => d.total));
                  return data.dataRate.map((pt, i) => (
                    <div
                      key={i}
                      className="flex-1 flex flex-col items-center relative"
                      title={`${pt.time} — ${pt.heartbeats} HB, ${pt.inventories} INV`}
                    >
                      <div
                        className="w-full flex flex-col justify-end"
                        style={{
                          height: 100,
                        }}
                      >
                        <div
                          className="w-full bg-blue-500"
                          style={{
                            borderRadius: '2px 2px 0 0',
                            height: `${(pt.heartbeats / maxVal) * 100}%`,
                            minHeight: pt.heartbeats > 0 ? 2 : 0,
                            transition: 'height .3s',
                          }}
                        />
                        <div
                          className="w-full"
                          style={{
                            background: '#8b5cf6',
                            borderRadius: '0 0 2px 2px',
                            height: `${(pt.inventories / maxVal) * 100}%`,
                            minHeight: pt.inventories > 0 ? 2 : 0,
                            transition: 'height .3s',
                          }}
                        />
                      </div>
                      {i % 10 === 0 ? (
                        <div
                          className="text-[9px] text-muted-2 mt-1 absolute whitespace-nowrap"
                          style={{
                            bottom: -16,
                          }}
                        >
                          {pt.time}
                        </div>
                      ) : null}
                    </div>
                  ));
                })()}
              </div>
              <div className="flex gap-4 mt-5 text-[11px] text-muted">
                <div
                  className="flex items-center"
                  style={{
                    gap: 5,
                  }}
                >
                  <div
                    className="h-2.5 bg-blue-500 rounded-sm"
                    style={{
                      width: 10,
                    }}
                  />
                  Heartbeats
                </div>
                <div
                  className="flex items-center"
                  style={{
                    gap: 5,
                  }}
                >
                  <div
                    className="h-2.5 rounded-sm"
                    style={{
                      width: 10,
                      background: '#8b5cf6',
                    }}
                  />
                  Inventaires
                </div>
                <div
                  className="font-mono"
                  style={{
                    marginLeft: 'auto',
                  }}
                >{`Total: ${data.dataRate.reduce((s, d) => s + d.total, 0)} / heure`}</div>
              </div>
            </div>
          )
        }
        {renderDevicesTable()}
      </div>
    );
  };

  // Tableau d'appareils \u2014 extrait pour \u00eatre r\u00e9utilis\u00e9 par le tableau de bord
  // ET la vue d\u00e9di\u00e9e /devices. Garde toute la logique de tri/filtrage en
  // closure sur l'\u00e9tat du composant Dashboard (devSort, devFilter, devTableRef).
  const renderDevicesTable = () => {
    if (!data) return null;
    const cols = [
              {
                key: 'computerName',
                label: 'Nom',
              },
              {
                key: 'clientName',
                label: 'Client',
              },
              {
                key: 'siteName',
                label: 'Site',
              },
              {
                key: 'machineType',
                label: 'Type',
              },
              {
                // Trié sur osName, pas sur platform : « windows / linux » ne
                // distingue rien, alors que trier par OS regroupe les Windows 10
                // entre eux — c'est ce qu'on cherche quand on traque un parc à
                // migrer.
                key: 'osName',
                label: 'OS',
              },
              {
                key: 'osArchitecture',
                label: 'Arch',
              },
              {
                key: 'cpuPercent',
                icon: Cpu,
                tip: 'CPU',
              },
              {
                key: 'memoryPercent',
                icon: MemoryStick,
                tip: 'M\u00e9moire',
              },
              {
                key: 'diskPercent',
                icon: HardDrive,
                tip: 'Disque',
              },
              {
                key: 'pendingUpdates',
                icon: RefreshCw,
                tip: 'Mises \u00e0 jour en attente (KB Windows ou paquets apt/dnf)',
              },
              {
                key: 'agentVersion',
                label: 'Agent',
              },
              {
                key: 'isOnline',
                label: 'Statut',
              },
              {
                key: null,
                label: '',
              },
            ];
            const sortedFiltered = (() => {
              let list = data.devices.filter(
                (d) =>
                  (devFilter.status === 'all' ||
                    (devFilter.status === 'online' ? d.isOnline : !d.isOnline)) &&
                  (devFilter.clientId === 'all' || d.clientId === devFilter.clientId) &&
                  (devFilter.machineType === 'all' || d.machineType === devFilter.machineType) &&
                  (devFilter.channel === 'all' ||
                    (devFilter.channel === 'pilots'
                      ? isPilot(d.updateChannel)
                      : normalizeChannel(d.updateChannel) === devFilter.channel)) &&
                  (d.cpuPercent ?? 0) >= devFilter.cpu &&
                  (d.memoryPercent ?? 0) >= devFilter.mem &&
                  (d.diskPercent ?? 0) >= devFilter.disk &&
                  (d.pendingUpdates ?? 0) >= devFilter.updates &&
                  (devFilter.search === '' ||
                    (
                      (d.label || '') +
                      ' ' +
                      (d.computerName || '') +
                      ' ' +
                      (d.ipAddress || '') +
                      ' ' +
                      (d.osName || '')
                    )
                      .toLowerCase()
                      .includes(devFilter.search.toLowerCase()))
              );
              const k = devSort.key,
                d = devSort.dir === 'asc' ? 1 : -1;
              if (k) {
                list = [...list].sort((a, b) => {
                  // « Statut » se trie sur le dernier contact (ici `lastSeen`), pas
                  // sur le booléen : sinon toutes les machines hors ligne sont à
                  // égalité. Cf. onlineSortValue dans lib/format.jsx.
                  // « Type » se trie sur l'étiquette affichée, pas sur la valeur
                  // brute : « Server » précède « Workstation » en anglais quand
                  // « Serveur » suit « Poste de travail » en français, et la flèche
                  // semblerait alors inversée.
                  const valeurTri = (row) =>
                    k === 'isOnline'
                      ? onlineSortValue(row.isOnline, row.lastSeen)
                      : k === 'machineType'
                        ? fmtMachineType(row.machineType)
                        : (row[k] ?? '');
                  const va = valeurTri(a),
                    vb = valeurTri(b);
                  if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * d;
                  if (typeof va === 'boolean') return (va === vb ? 0 : va ? 1 : -1) * d;
                  return (
                    String(va).localeCompare(String(vb), 'fr', {
                      numeric: true,
                    }) * d
                  );
                });
              }
              return list;
            })();
            const filterBtnCls = (active) =>
              `px-2.5 py-1 rounded-md border text-[11px] cursor-pointer transition-colors ${
                active
                  ? 'border-blue-500 bg-blue-500/[.12] text-blue-400 font-semibold hover:bg-blue-500/[.18]'
                  : 'border-rule bg-transparent text-muted hover:bg-card-hi hover:text-fg-2 hover:border-rule-2'
              }`;
            return (
              <div
                ref={devTableRef}
                className="bg-card border border-rule rounded-2xl overflow-hidden"
                style={{
                  scrollMarginTop: 16,
                }}
              >
                <div className="py-3.5 px-5 border-b border-rule text-[13px] font-semibold text-fg-2 flex items-center gap-3 flex-wrap">
                  <span>Appareils</span>
                  <span className="text-[11px] text-muted-2 font-normal">
                    {sortedFiltered.length + ' / ' + data.devices.length}
                  </span>
                </div>{' '}
                <div className="py-2.5 px-5 border-b border-rule flex items-center gap-2.5 flex-wrap bg-sidebar">
                  <input
                    placeholder="Rechercher (nom, IP, OS)..."
                    value={devFilter.search}
                    onChange={(e) =>
                      setDevFilter({
                        ...devFilter,
                        search: e.target.value,
                      })
                    }
                    className="min-w-[180px] rounded-md border border-rule bg-card text-fg-2 text-xs outline-none"
                    style={{
                      flex: '1 1 200px',
                      padding: '5px 10px',
                    }}
                  />
                  <span className="text-[11px] text-muted-2">Statut:</span>
                  {[
                    ['all', 'Tous'],
                    ['online', 'En ligne'],
                    ['offline', 'Hors ligne'],
                  ].map(([v, l]) => (
                    <button
                      key={v}
                      onClick={() =>
                        setDevFilter({
                          ...devFilter,
                          status: v,
                        })
                      }
                      className={filterBtnCls(devFilter.status === v)}
                    >
                      {l}
                    </button>
                  ))}
                  <span
                    className="text-[11px] text-muted-2"
                    style={{
                      marginLeft: 6,
                    }}
                  >
                    Type:
                  </span>
                  {[
                    ['all', 'Tous'],
                    ['Server', 'Serveur'],
                    ['Workstation', 'Poste de travail'],
                  ].map(([v, l]) => (
                    <button
                      key={v}
                      onClick={() =>
                        setDevFilter({
                          ...devFilter,
                          machineType: v,
                        })
                      }
                      className={filterBtnCls(devFilter.machineType === v)}
                    >
                      {l}
                    </button>
                  ))}
                  <span
                    className="text-[11px] text-muted-2"
                    style={{
                      marginLeft: 6,
                    }}
                  >
                    Canal:
                  </span>
                  {[
                    ['all', 'Tous'],
                    ['pilots', 'Pilotes'],
                    ['pilot1', 'Pilote 1'],
                    ['pilot2', 'Pilote 2'],
                    ['pilot3', 'Pilote 3'],
                  ].map(([v, l]) => (
                    <button
                      key={v}
                      onClick={() =>
                        setDevFilter({
                          ...devFilter,
                          channel: v,
                        })
                      }
                      className={filterBtnCls(devFilter.channel === v)}
                    >
                      {l}
                    </button>
                  ))}
                  <span
                    className="text-[11px] text-muted-2"
                    style={{
                      marginLeft: 6,
                    }}
                  >
                    Client:
                  </span>
                  <div className="min-w-[180px]">
                    <ClientPicker
                      value={devFilter.clientId === 'all' ? null : devFilter.clientId}
                      onChange={(id) =>
                        setDevFilter({
                          ...devFilter,
                          clientId: id || 'all',
                        })
                      }
                      placeholder="Tous les clients"
                    />
                  </div>
                  {[
                    { k: 'cpu', Icon: Cpu, tip: 'CPU' },
                    { k: 'mem', Icon: MemoryStick, tip: 'M\u00e9moire' },
                    { k: 'disk', Icon: HardDrive, tip: 'Disque' },
                  ].map(({ k, Icon, tip }) => (
                    <div key={k} className="flex items-center gap-1" title={tip + ' \u2265 seuil'}>
                      <Icon size={13} strokeWidth={1.75} className="text-muted-2" />
                      <span className="text-[11px] text-muted-2">{'\u2265'}</span>
                      {[0, 50, 80, 95].map((t) => (
                        <button
                          key={t}
                          onClick={() =>
                            setDevFilter({
                              ...devFilter,
                              [k]: t,
                            })
                          }
                          className={filterBtnCls(devFilter[k] === t)}
                        >
                          {t === 0 ? 'tous' : t + '%'}
                        </button>
                      ))}
                    </div>
                  ))}
                  <div
                    className="flex items-center gap-1"
                    title="Mises à jour Windows en attente ≥ seuil"
                  >
                    <RefreshCw size={13} strokeWidth={1.75} className="text-muted-2" />
                    <span className="text-[11px] text-muted-2">≥</span>
                    {[0, 1, 10].map((t) => (
                      <button
                        key={t}
                        onClick={() =>
                          setDevFilter({
                            ...devFilter,
                            updates: t,
                          })
                        }
                        className={filterBtnCls(devFilter.updates === t)}
                      >
                        {t === 0 ? 'tous' : t}
                      </button>
                    ))}
                  </div>
                  {(devFilter.status !== 'all' ||
                    devFilter.clientId !== 'all' ||
                    devFilter.machineType !== 'all' ||
                    devFilter.channel !== 'all' ||
                    devFilter.cpu ||
                    devFilter.mem ||
                    devFilter.disk ||
                    devFilter.updates ||
                    devFilter.search) && (
                    <button
                      onClick={() =>
                        setDevFilter({
                          status: 'all',
                          clientId: 'all',
                          machineType: 'all',
                          channel: 'all',
                          cpu: 0,
                          mem: 0,
                          disk: 0,
                          updates: 0,
                          search: '',
                        })
                      }
                      className="px-2.5 py-1 rounded-md border border-crit/40 text-crit inline-flex items-center gap-1.5 text-[11px] cursor-pointer transition-colors hover:bg-crit/10"
                    >
                      <X size={12} strokeWidth={2} /> Réinitialiser
                    </button>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table
                    className="w-full text-[12.5px]"
                    style={{
                      borderCollapse: 'collapse',
                    }}
                  >
                    <thead>
                      <tr className="border-b border-rule">
                        {cols.map((col, i) => {
                          const isActive = devSort.key === col.key;
                          const arrow = isActive ? (devSort.dir === 'asc' ? ' \u25b2' : ' \u25bc') : '';
                          const Icon = col.icon;
                          return (
                            <th
                              key={col.label || col.tip || 'spacer-' + i}
                              onClick={col.key ? () => toggleSort(col.key) : undefined}
                              title={col.tip || undefined}
                              className="text-left text-[11px] uppercase whitespace-nowrap"
                              style={{
                                padding: '9px 14px',
                                color: isActive ? '#60a5fa' : '#64748b',
                                fontWeight: isActive ? 600 : 500,
                                cursor: col.key ? 'pointer' : 'default',
                                userSelect: 'none',
                              }}
                            >
                              {Icon ? (
                                <span className="inline-flex items-center gap-0.5">
                                  <Icon size={14} strokeWidth={1.75} />
                                  <span>{arrow}</span>
                                </span>
                              ) : (
                                <>
                                  {col.label}
                                  {arrow}
                                </>
                              )}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedFiltered.length === 0 ? (
                        <tr>
                          <td
                            colSpan={cols.length}
                            className="text-muted-2 text-xs text-center"
                            style={{
                              padding: '30px 20px',
                            }}
                          >
                            Aucun appareil ne correspond aux filtres.
                          </td>
                        </tr>
                      ) : null}
                      {sortedFiltered.map((d) => (
                        <tr
                          key={d.id}
                          onClick={() =>
                            navigate('deviceDetail', {
                              device: d.id,
                            })
                          }
                          className="border-b border-rule cursor-pointer"
                          style={{
                            transition: 'background .15s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(59,130,246,0.06)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <td className="py-2.5 px-3.5 font-mono text-xs">
                            <div className="font-semibold text-fg-2">{d.label || d.computerName}</div>
                            {d.label ? (
                              <div
                                className="text-[10px] text-muted-2"
                                style={{
                                  marginTop: 1,
                                }}
                              >
                                {d.computerName}
                              </div>
                            ) : null}
                          </td>
                          <td className="py-2.5 px-3.5 text-muted text-[11.5px]">{d.clientName}</td>
                          <td className="py-2.5 px-3.5 text-muted text-[11.5px]">{d.siteName}</td>
                          <td className="py-2.5 px-3.5">
                            <MachineTypeBadge machineType={d.machineType} platform={d.platform} />
                          </td>
                          <td className="py-2.5 px-3.5">
                            {/* Les agents antérieurs à la 1.9 n'envoient pas la
                                plateforme : l'absence vaut « windows », comme côté
                                serveur. */}
                            <div
                              className="flex items-center gap-1.5 max-w-[210px]"
                              title={d.osName || undefined}
                            >
                              {/* L'icône remplace l'ancien badge « Win »/« Linux » :
                                  avec le nom d'OS affiché juste à côté, le badge
                                  répétait l'information et mangeait la largeur. */}
                              <span
                                className={`shrink-0 ${
                                  d.platform === 'linux'
                                    ? 'text-amber-300'
                                    : d.platform === 'routeros'
                                      ? 'text-violet-400'
                                      : 'text-blue-400'
                                }`}
                              >
                                {d.platform === 'linux' ? (
                                  <TuxIcon size={13} />
                                ) : d.platform === 'routeros' ? (
                                  <RouterIcon size={13} />
                                ) : (
                                  <WindowsIcon size={12} />
                                )}
                              </span>
                              <span className="truncate text-[11.5px] text-muted">
                                {fmtOsName(d.osName) || '—'}
                              </span>
                            </div>
                          </td>
                          <td className="py-2.5 px-3.5">
                            {d.osArchitecture ? (
                              <span
                                className="py-0.5 px-2 rounded text-[10px] font-semibold font-mono"
                                style={{
                                  background: (d.osArchitecture || '').toLowerCase().includes('arm')
                                    ? 'rgba(249,115,22,0.15)'
                                    : 'rgba(59,130,246,0.15)',
                                  color: (d.osArchitecture || '').toLowerCase().includes('arm')
                                    ? '#fb923c'
                                    : '#60a5fa',
                                }}
                              >
                                {d.osArchitecture}
                              </span>
                            ) : (
                              <span className="text-muted-3 text-[10px]">—</span>
                            )}
                          </td>
                          {[d.cpuPercent, d.memoryPercent, d.diskPercent].map((p, i) => (
                            <td
                              key={i}
                              className="py-2.5 px-3.5"
                              style={{
                                width: 120,
                              }}
                            >
                              <div className="flex items-center gap-1.5">
                                <div className="flex-1">{PBar(p)}</div>
                                <span
                                  className="text-[11px] text-muted font-mono text-right"
                                  style={{
                                    minWidth: 32,
                                  }}
                                >{`${p.toFixed ? p.toFixed(0) : p}%`}</span>
                              </div>
                            </td>
                          ))}
                          <td
                            className="py-2.5 px-3.5 font-mono text-[11px]"
                            style={{
                              color:
                                d.pendingUpdates > 10
                                  ? '#fca5a5'
                                  : d.pendingUpdates > 0
                                    ? '#fcd34d'
                                    : '#86efac',
                            }}
                          >
                            {d.pendingUpdates}
                          </td>
                          <td className="py-2.5 px-3.5 font-mono text-[11px] text-muted">
                            <span className="inline-flex items-center gap-1.5">
                              {d.agentVersion || '—'}
                              {isPilot(d.updateChannel) && <PilotBadge channel={d.updateChannel} />}
                            </span>
                          </td>
                          <td className="py-2.5 px-3.5">
                            <div
                              className="flex items-center"
                              style={{
                                gap: 5,
                              }}
                            >
                              <div
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  background: d.isOnline ? '#22c55e' : '#ef4444',
                                }}
                              />
                              <span
                                className={`text-[11px] ${d.isOnline ? 'text-ok' : 'text-crit'}`}
                                title={!d.isOnline && d.lastSeen ? `Dernier heartbeat : ${new Date(d.lastSeen).toLocaleString('fr-FR')}` : undefined}
                              >
                                {fmtOnlineStatus(d.isOnline, d.lastSeen)}
                              </span>
                            </div>
                          </td>
                          <td className="py-2.5 px-3.5 text-right">
                            {!d.isOnline && can('canDeleteDevices') ? (
                              <button
                                title="Supprimer cet agent hors-ligne"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const name = d.label || d.computerName;
                                  if (
                                    !confirm(
                                      `Supprimer définitivement l'agent "${name}" et toutes ses données (inventaire, alertes, historique CVE, heartbeats) ?\n\nCette action est irréversible.`
                                    )
                                  )
                                    return;
                                  const ok = await api.del('/devices/' + d.id);
                                  if (!ok) {
                                    alert(
                                      'Échec de la suppression — vérifier les permissions ou les logs serveur.'
                                    );
                                    return;
                                  }
                                  setData((prev) =>
                                    prev
                                      ? {
                                          ...prev,
                                          devices: prev.devices.filter((x) => x.id !== d.id),
                                        }
                                      : prev
                                  );
                                }}
                                className="py-1 px-1.5 rounded-md bg-transparent border border-crit/40 text-crit text-[11px] cursor-pointer inline-flex items-center hover:bg-crit/10"
                              >
                                <X size={12} strokeWidth={2} />
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
  };

  // Vue dédiée /devices : titre + tableau d'appareils, sans les autres
  // composants du tableau de bord (stats, alertes, graphique de flux).
  const renderDevices = () => {
    if (!data) return <div className="text-muted-2">Chargement...</div>;
    return (
      <div>
        <h1 className="text-[22px] font-bold text-fg mb-5">Appareils</h1>
        {renderDevicesTable()}
      </div>
    );
  };

  const renderConfig = () => <ConfigPage />;
  const renderDeviceDetail = () => (
    <DeviceDetail
      agentId={selectedDevice}
      user={user}
      perms={perms}
      onBack={() =>
        navigate(
          selectedClient ? 'clientDetail' : 'dashboard',
          selectedClient ? { client: selectedClient } : {}
        )
      }
    />
  );
  const renderClients = () => (
    <ClientsView onSelectClient={(id) => navigate('clientDetail', { client: id })} can={can} />
  );
  const renderClientDetail = () => (
    <ClientDetail
      clientId={selectedClient}
      onBack={() => navigate('clients')}
      onSelectSite={() => {}}
      onSelectDevice={(id) => navigate('deviceDetail', { device: id })}
      onShowAlerts={(siteId) => goToAlerts(null, null, { clientId: selectedClient, siteId })}
    />
  );
  const renderProvisions = () => <ProvisioningView />;
  const renderUsers = () => <UsersPage />;
  const renderAlerts = () => (
    <AlertsPage
      key={alertsRequest.key}
      initialSeverity={alertsRequest.severity}
      initialCategory={alertsRequest.category}
      initialClientId={alertsRequest.clientId}
      initialSiteId={alertsRequest.siteId}
    />
  );
  const renderGroups = () => <GroupsPage />;
  const renderRoles = () => <RolesPage />;
  const renderProfile = () => <ProfilePage />;
  const renderAudit = () => <AuditPage />;
  const renderSearch = () => <SearchPage />;
  const renderVulnerabilities = () => (
    <VulnerabilitiesPage
      key={vulnsRequest.key}
      initialSeverity={vulnsRequest.severity}
      initialAgentId={vulnsRequest.agentId}
      initialAgentName={vulnsRequest.agentName}
    />
  );

  const viewContent = {
    dashboard: renderDashboard,
    devices: renderDevices,
    config: renderConfig,
    deviceDetail: renderDeviceDetail,
    clients: renderClients,
    clientDetail: renderClientDetail,
    provisions: renderProvisions,
    users: renderUsers,
    alerts: renderAlerts,
    groups: renderGroups,
    roles: renderRoles,
    profile: renderProfile,
    audit: renderAudit,
    search: renderSearch,
    vulnerabilities: renderVulnerabilities,
    ai: () => <AiReportsPage />,
    mikrotik: () => <MikrotikPage />,
    websites: () => <WebMonitorsPage />,
  };

  const ctxValue = { user, perms, reloadPerms, navigate, can, goToVulns };
  return (
    <DashboardContext.Provider value={ctxValue}>
      <div
        className="flex w-full bg-app"
        style={{
          minHeight: '100vh',
        }}
      >
        {/* Hamburger : visible seulement en mobile */}
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="md:hidden fixed top-3 left-3 z-30 bg-card border border-rule rounded-lg p-2 text-fg-2 shadow-lg"
          aria-label="Ouvrir le menu"
        >
          <Menu size={20} strokeWidth={1.75} />
        </button>
        {/* Backdrop sombre sous mobile quand le menu est ouvert */}
        {mobileMenuOpen && (
          <div
            onClick={() => setMobileMenuOpen(false)}
            className="md:hidden fixed inset-0 bg-black/60 z-30"
            aria-hidden="true"
          />
        )}
        <div className={S.sidebarCls(mobileMenuOpen)}>
          <div className="border-b border-rule px-4 pt-8 pb-2 flex flex-col items-center">
            <img src={brandLogo} alt="Fonxtio — propulsé par Telipso" className="h-10 max-w-full" />
            {/* Injectée au build depuis dashboard/package.json (cf. vite.config.js),
                que deploy.sh aligne sur la version de l'agent. Ne pas réécrire en dur. */}
            <div className="text-[10px] text-muted-2 font-mono mt-2">v{__APP_VERSION__}</div>
          </div>
          <nav className="flex-1 py-2.5 px-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => navigate(item.id)}
                  className={S.navBtnCls(
                    view === item.id ||
                      (item.id === 'clients' && view === 'clientDetail') ||
                      (item.id === 'devices' && view === 'deviceDetail')
                  )}
                >
                  <Icon size={16} strokeWidth={1.75} className="shrink-0" />
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.badge > 0 && (
                    <span
                      className="shrink-0 min-w-[18px] h-[16px] px-1 rounded-full bg-crit/15 text-crit text-[10px] font-bold font-mono flex items-center justify-center leading-none"
                      title={item.id === 'alerts' ? 'Alertes critiques ouvertes' : 'Appareils hors ligne'}
                    >
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
          <div
            className="py-3 px-3.5 border-t border-rule flex items-center"
            style={{
              gap: 9,
            }}
          >
            <div
              className="w-[30px] h-[30px] shrink-0 rounded-lg flex items-center justify-center text-[11px] font-bold text-white"
              style={{
                background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)',
              }}
            >
              {user?.displayName
                ?.split(' ')
                .map((w) => w[0])
                .join('')
                .slice(0, 2) || 'AD'}
            </div>
            <div
              className="flex-1 min-w-0 cursor-pointer"
              onClick={() => navigate('profile')}
              title={(user?.displayName || 'Admin') + (user?.email ? ' — ' + user.email : '')}
            >
              <div className="text-[11.5px] font-semibold text-fg-2 truncate">
                {user?.displayName || 'Admin'}
              </div>
              <div className="text-[10px] text-muted-2 truncate">{user?.email || ''}</div>
            </div>
            <ThemeToggle className="p-1" />
            <button
              onClick={() => navigate('profile')}
              title="Profil & 2FA"
              className="border-none bg-transparent text-muted-2 cursor-pointer p-1 hover:text-fg-2"
            >
              <Cog size={16} strokeWidth={1.75} />
            </button>
            <button
              title="Se déconnecter"
              onClick={() => {
                if (confirm('Se d\u00e9connecter ?')) {
                  disconnectRealtime();
                  logout();
                }
              }}
              className="border-none bg-transparent text-muted-2 cursor-pointer p-1 hover:text-fg-2"
            >
              <Power size={16} strokeWidth={1.75} />
            </button>
          </div>
        </div>{' '}
        <main className={S.mainCls}>
          {loading ? (
            <div className="text-muted-2 p-10 text-center">Chargement des données...</div>
          ) : (
            (viewContent[view] || renderDashboard)()
          )}
        </main>
        {/* Cloche d'alertes flottante, bas-droite : hors du flux de la page pour
            ne pas consommer de hauteur au-dessus de chaque vue. Le point de
            couleur sur la cloche porte l'état de la connexion temps réel
            (libellé en infobulle). Le menu s'ouvre vers le HAUT. Présente sur
            toutes les vues, donc hors du "Chargement..." qui ne porte que sur le
            contenu de la vue. */}
        {can('ViewDevices') && (
          <div className="fixed bottom-4 right-4 z-[110]">
            <button
              onClick={toggleBell}
              title={`Alertes récentes — ${RT_STATE_LABEL[rtState] || 'Temps réel'}`}
              className="relative w-11 h-11 rounded-full bg-card border border-rule shadow-2xl text-muted-2 cursor-pointer flex items-center justify-center hover:text-fg-2 hover:bg-card-hi"
            >
              <Bell size={18} strokeWidth={1.75} />
              {unreadAlertsCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[17px] h-[17px] px-[4px] rounded-full bg-crit text-white text-[9.5px] font-bold flex items-center justify-center leading-none border-2 border-card">
                  {unreadAlertsCount > 9 ? '9+' : unreadAlertsCount}
                </span>
              )}
              <span
                className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-card ${RT_STATE_DOT[rtState] || 'bg-muted-2'}`}
              />
            </button>
            {bellOpen && (
              <>
                {/* Zone invisible pour fermer au clic extérieur — même
                    principe que le backdrop du menu mobile. */}
                <div className="fixed inset-0 z-40" onClick={() => setBellOpen(false)} />
                    <div className="absolute right-0 bottom-full mb-2 w-80 max-w-[90vw] bg-card border border-rule rounded-xl shadow-2xl z-50 overflow-hidden">
                      <div className="px-3.5 py-2.5 border-b border-rule text-[12px] font-semibold text-fg-2">
                        Alertes récentes
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {bellAlerts.length === 0 ? (
                          <div className="px-3.5 py-6 text-center text-muted-2 text-[11.5px]">
                            Aucune alerte ouverte.
                          </div>
                        ) : (
                          bellAlerts.slice(0, 6).map((a) => (
                            <div key={a.id} className="px-3.5 py-2.5 border-b border-rule last:border-b-0">
                              <div className={`text-[11.5px] font-semibold ${a.severity === 2 ? 'text-crit' : 'text-warn'}`}>
                                {a.title}
                              </div>
                              <div className="text-[10.5px] text-muted-2 mt-0.5 truncate">
                                {a.computerName || a.clientName || ''}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setBellOpen(false);
                          goToAlerts(null, null);
                        }}
                        className="w-full text-center py-2 text-[11.5px] text-blue-400 font-semibold bg-transparent border-none cursor-pointer hover:bg-card-hi"
                      >
                        Voir toutes les alertes
                      </button>
                    </div>
              </>
            )}
          </div>
        )}
        {/* Toasts temps réel — pile bas-droite AU-DESSUS de la cloche, auto-disparition ~6 s. */}
        <div className="fixed bottom-[72px] right-4 z-[100] flex flex-col gap-2 w-[320px] max-w-[92vw]">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`bg-card border ${TONE_BORDER[t.tone] || 'border-rule'} rounded-xl shadow-2xl px-3.5 py-3 flex items-start gap-2.5`}
            >
              <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${TONE_DOT[t.tone] || 'bg-muted-2'}`} />
              <div className="flex-1 min-w-0">
                <div className={`text-[12px] font-semibold ${TONE_TEXT[t.tone] || 'text-fg-2'}`}>{t.title}</div>
                <div className="text-[11px] text-muted mt-0.5 break-words">{t.message}</div>
              </div>
              <button
                onClick={() => dismissToast(t.id)}
                className="text-muted-2 hover:text-fg-2 bg-transparent border-none cursor-pointer p-0.5 shrink-0"
                aria-label="Fermer"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </DashboardContext.Provider>
  );
}
